import * as THREE from 'three';
import { BRIDGE_ROWS, bridgeLayout, hexToWorld, plankAt, ridgeLayout, walkLayout, walkLit, WALK_BACK, WALK_DOWN, WALK_ON, type BattleState, type LitterRules, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, SnowFall } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The cleric's last road, chapter 2 (docs/sendas/clerigo.md, «La pasarela»): the cliff of the Spine's south face under a
 * full moon, over a sea of fog. The board's tiles are put away: what can be walked is drawn as ledges of pale rock under
 * snow (each with its rock going down into the fog) or as planks; the rest is the cliff rising on the west, or the drop
 * to the fog on the east. Far below, the fog moves slowly, silver where the moon lies on it.
 *
 * - `ridgePath` (act 1) and `ridgeFoot` (the opening): the narrow way along the face, its chapels cut into the rock (a
 *   shrine with a slate roof over the way, its lamp, the shadow where stones don't reach drawn faint on the snow), the
 *   slingers' ledge across the fog. A volley warned: a red ring where it will land on the hero.
 * - `moonWalk` (act 2, the puzzle), `moonMist` (the cinematic) and `walkFire` (the campfire): four spans of planks nailed
 *   to the cliff between landings, braced into the rock; on each landing a post with its steel mirror. The moon comes
 *   in as a shaft of light onto the first post; from post to post high, and laid down along the span of the post that
 *   is down (a band of white on the planks), back to the moon from one turned back. The planks under ink are black and
 *   wet-violet; lit, the ink burns off with sparks; dark again, it creeps back. A gargoyle on a plank makes it pulse red.
 * - `chainBridge` (act 3, the test) and `bridgeChapel` (the close): the planks hung from two chains between two crags with
 *   their chapels; a torn plank is gone (two stubs left), laid again it grows back; the mangonel's stone falls on an arc
 *   onto its shadow (a dark disc on the planks that grows while it comes).
 *
 * Draw calls: the dressing (one merged mesh), the planks (one instanced mesh), the fog sea and two mist sheets, the moon
 * beam (one strip, rebuilt only when the mirrors change) and its shaft, the mirrors (a group each, four), the warning
 * discs (a few), the glow layer and the snow. No lights of its own: the board's one point light is the stretcher's
 * lantern (as in chapter 1).
 */

export const PASARELA_PLACES = new Set(['ridgePath', 'ridgeFoot', 'moonMist', 'moonWalk', 'walkFire', 'chainBridge', 'bridgeChapel']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const WHITE = new THREE.Color('#f4f8ff');
const MOON = new THREE.Color('#dfe8ff');
const COLD = new THREE.Color('#b9ccff');
const WARM = new THREE.Color('#ffe2c4');
const RED = new THREE.Color('#ff4f3a');
const INK = new THREE.Color('#6a4aa8');
const ROCK = ['#6d7482', '#626977', '#788090', '#5a616e'];
const ROCK_D = ['#4b515d', '#454b57', '#525965'];
const SNOW = ['#d6deea', '#c8d2e0', '#dfe6f0'];
const WOOD = ['#6e5c4c', '#634f40', '#776453'];
const WOOD_D = '#3e3229';
const SLATE = ['#3a4049', '#323840', '#424852'];
const STONE = ['#8a8d93', '#7f8289', '#959aa1'];
const IRON = '#2c3036';
const PINE = ['#2f3f36', '#2a3a31', '#34453b'];
const INK_PLANK = new THREE.Color('#150f1f');
const WOOD_LIT = new THREE.Color('#8a7663');
const WOOD_DARK = new THREE.Color('#5d4c3f');

type Kind = 'walk' | 'plank' | 'cliff' | 'void' | 'ledge';
type Hour = 'night';

const allCells = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) out.push({ q, r });
  return out;
};

/** The scenes' boards (game/cine.ts): the foot of the cliff path, the walkway, the campfire's landing, the far chapel. */
export const pasarelaSceneMap = (place0: string): MapDef | null => {
  if (!PASARELA_PLACES.has(place0) || place0 === 'ridgePath' || place0 === 'moonWalk' || place0 === 'chainBridge') return null;
  if (place0 === 'moonMist') {
    const L = walkLayout(3);
    return { radius: L.radius + 2, bastion: L.radius, sectors: [], kind: 'incursion', open: true, rocks: L.rocks };
  }
  const R = 6;
  const rocks = allCells(R).filter((h) => {
    const p = hexToWorld(h.q, h.r);
    if (place0 === 'ridgeFoot') return p.x > 1700 || p.x < -1900;
    if (place0 === 'walkFire') return p.x > 2600 || p.x < -2600 || h.r <= -4;
    // The far chapel's yard: the chapel at the top, the bridge going down to the south, the drop either side of it.
    return h.r <= -4 || (h.r >= 2 && Math.abs(p.x) > 600) || Math.abs(p.x) > 2600;
  });
  return { radius: R + 2, bastion: R, sectors: [], kind: 'incursion', open: true, rocks };
};

const FOG_VERT = /* glsl */ `varying vec2 vP; void main() { vec4 w = modelMatrix * vec4(position, 1.0); vP = w.xz; gl_Position = projectionMatrix * viewMatrix * w; }`;
interface PostView {
  g: THREE.Group;
  disc: THREE.Group;
  yaw: number;
  tilt: number;
  wantYaw: number;
  wantTilt: number;
  top: THREE.Vector3;
}

export class PasarelaLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(220);
  private dome: SkyDome | null = null;
  private fall: SnowFall | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private kinds = new Map<string, Kind>();
  private fogMat: THREE.ShaderMaterial | null = null;
  private tex: THREE.DataTexture | null = null;
  private mistMat: THREE.ShaderMaterial | null = null;
  // The stretcher's pool of light.
  private pool: THREE.Mesh | null = null;
  private poolMat: THREE.ShaderMaterial | null = null;
  // The planks: one instance a board; each board's plank (its index in the sim) and its place at rest.
  private planks: THREE.InstancedMesh | null = null;
  private boards: { plank: number; m: THREE.Matrix4; at: THREE.Vector3; yaw: number; len: number; side: number }[] = [];
  private plankKey = '';
  // The walkway's posts, the moon's beam.
  private posts: PostView[] = [];
  private beam: THREE.Mesh | null = null;
  private beamMat: THREE.ShaderMaterial | null = null;
  private beamKey = '';
  private shaft: THREE.Mesh | null = null;
  private moonTop = new THREE.Vector3();
  private spanEnds: THREE.Vector3[][] = [];
  // The warnings: a ring under the hero for the volley, a shadow for each stone, the chapels' shelter.
  private warnMat: THREE.ShaderMaterial | null = null;
  private warns: THREE.Mesh[] = [];
  private shadeMat: THREE.ShaderMaterial | null = null;
  private stone: THREE.Mesh | null = null;
  private lastState: BattleState | null = null;
  private chapels: THREE.Vector3[] = [];
  private ledgeX = 0;
  private v3 = new THREE.Vector3();
  private m4 = new THREE.Matrix4();
  private c = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `pasarela-${place}`;
    this.classify(s);
    const kit = new Kit();
    this.ground(kit);
    if (place === 'ridgePath' || place === 'ridgeFoot') this.buildRidge(kit, s);
    else if (place === 'moonWalk' || place === 'moonMist') this.buildWalk(kit, s);
    else if (place === 'walkFire') this.buildFireLanding(kit);
    else this.buildBridge(kit, s);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.03, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'pasarela-dress');
    if (dress) {
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.buildFog();
    if (s.rules?.senda?.litter) this.buildPool();
    this.buildWarnings();
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: color.clone(), size, a, seed: this.lights.length * 1.37, flick });
  }

  /** A deterministic jitter (looks only). */
  private k(q: number, r: number, salt = 0): number {
    return Math.sin(q * 12.9898 + r * 78.233 + salt * 37.7) * 0.5 + 0.5;
  }

  private kindAt(q: number, r: number): Kind {
    return this.kinds.get(`${q},${r}`) ?? 'void';
  }

  /* ---------- what each cell is ---------- */

  private classify(s: BattleState): void {
    const R = this.map.radius;
    const rocks = new Set((this.map.rocks ?? []).map((h) => `${h.q},${h.r}`));
    const cells = allCells(R + 3);
    const planks = new Set<string>();
    let westOf: (q: number, r: number, x: number) => boolean = (_q, _r, x) => x < -2200;
    if (this.place === 'ridgePath') {
      const L = ridgeLayout(s.seed);
      const mid = new Map<number, number>();
      for (const p of L.way) mid.set(Math.round(p.y / 866), p.x);
      westOf = (_q, r, x) => x < (mid.get(Math.max(-(L.radius - 1), Math.min(L.radius - 1, r))) ?? -1500) - 300;
      this.ledgeX = L.ledge;
    } else if (this.place === 'moonWalk' || this.place === 'moonMist') {
      const L = this.place === 'moonWalk' ? walkLayout(s.seed) : walkLayout(3);
      for (const sp of L.spans) for (const c of sp) planks.add(`${c.q},${c.r}`);
      const walk = new Map<number, number>();
      for (const h of [...L.landings, ...L.spans.flat()]) {
        const x = hexToWorld(h.q, h.r).x;
        for (let dr = -1; dr <= 1; dr++) walk.set(h.r + dr, Math.min(walk.get(h.r + dr) ?? 99999, x));
      }
      westOf = (_q, r, x) => x < (walk.get(r) ?? 0) - 700;
    } else if (this.place === 'chainBridge' || this.place === 'bridgeChapel') {
      if (this.place === 'chainBridge') for (const k of bridgeLayout(s.seed).planks) for (const c of k.cells) planks.add(`${c.q},${c.r}`);
      else for (const h of allCells(6)) if (h.r >= 2 && Math.abs(hexToWorld(h.q, h.r).x) <= 600) planks.add(`${h.q},${h.r}`);
      westOf = () => false;
    } else if (this.place === 'ridgeFoot') {
      this.ledgeX = 5200;
      westOf = (_q, _r, x) => x < -1900;
    } else if (this.place === 'walkFire') westOf = (_q, _r, x) => x < -2600;
    for (const h of cells) {
      const key = `${h.q},${h.r}`;
      const x = hexToWorld(h.q, h.r).x;
      const inBoard = Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) <= R - 2;
      let k: Kind;
      if (planks.has(key)) k = 'plank';
      else if (inBoard && !rocks.has(key)) k = 'walk';
      else if (westOf(h.q, h.r, x)) k = 'cliff';
      else if (this.ledgeX && Math.abs(x - this.ledgeX) < 700) k = 'ledge';
      else k = 'void';
      // (The walkway's scene: its chapel at the top stands on rock.)
      if (this.place === 'walkFire' && h.r <= -4 && Math.abs(x) <= 2600) k = 'cliff';
      if (this.place === 'bridgeChapel' && h.r <= -4 && Math.abs(x) <= 2600) k = 'cliff';
      this.kinds.set(key, k);
    }
  }

  /* ---------- the ground: ledges, the cliff, the rock going down ---------- */

  private ground(kit: Kit): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const slab = new THREE.CylinderGeometry(0.5, 0.56, 1, 9);
    const cone = new THREE.ConeGeometry(0.5, 1, 7);
    const pineCone = new THREE.ConeGeometry(0.5, 1, 7);
    const R = this.map.radius;
    for (const h of allCells(R + 3)) {
      const kind = this.kindAt(h.q, h.r);
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 1);
      const k2 = this.k(h.q, h.r, 2);
      if (kind === 'walk') {
        // One ground of snow, not tiles: wide thin caps at one height that run into each other, the rock under them.
        kit.add(slab, ROCK_D[Math.floor(k * 2.99)] as string, place(p.x, p.y - 0.32, p.z, k * 3, new THREE.Vector3(S * 1.02, 0.56, S * 0.98)));
        kit.add(slab, SNOW[Math.floor(k2 * 2.99)] as string, place(p.x, p.y - 0.03, p.z, k * 3, new THREE.Vector3(S * 1.32, 0.07, S * 1.28)));
        const open = ([[1, 0], [0, 1], [-1, 1], [-1, 0], [0, -1], [1, -1]] as const).filter(([dq, dr]) => this.kindAt(h.q + dq, h.r + dr) === 'void');
        if (open.length) {
          // Its rock going down into the fog: a crag under the ledge, banded with a little snow.
          kit.add(cone, ROCK[Math.floor(k2 * 3.99)] as string, place(p.x, p.y - 2.9, p.z, k * 4, new THREE.Vector3(S * 1.05, 5.2, S * 1.0), Math.PI));
          kit.add(slab, SNOW[1] as string, place(p.x, p.y - 0.7, p.z, k * 4, new THREE.Vector3(S * 0.86, 0.05, S * 0.82)));
          // The lip toward the drop: angular rock breaking the snow's edge, a little snow on it (the snow alone read as cloud).
          for (const [dq, dr] of open) {
            const n = this.hex(h.q + dq, h.r + dr);
            const e = p.clone().lerp(n, 0.46);
            const kk = this.k(h.q * 7 + dq, h.r * 7 + dr, 5);
            kit.add(ico, STONE[Math.floor(kk * 2.99)] as string, place(e.x, p.y - 0.12, e.z, kk * 6, new THREE.Vector3(0.62, 0.42 + kk * 0.2, 0.5)));
            kit.add(ico, SNOW[0] as string, place(e.x - (e.x - p.x) * 0.15, p.y + 0.08 + kk * 0.08, e.z - (e.z - p.z) * 0.15, kk * 6, new THREE.Vector3(0.42, 0.1, 0.34)));
          }
        }
      } else if (kind === 'cliff') {
        const into = this.cliffDepth(h.q, h.r);
        // (One height a step into the rock: heights by cell read as tiles.)
        const top = 2.4 + Math.min(2, into) * 0.3;
        if (into === 0) {
          // The face over the way: angular rock, steep, its facets catching the moon; a thin snow on each ledge of it.
          for (let i = 0; i < 2; i++) {
            const a = k * 6.28 + i * 2.4;
            const x = p.x + Math.cos(a) * 0.22;
            const z = p.z + Math.sin(a) * 0.2;
            const hy = top * (0.75 + i * 0.3);
            kit.add(ico, ROCK[(i + Math.floor(k * 4)) % 4] as string, place(x, p.y + hy * 0.5 - 0.5, z, a, new THREE.Vector3(S * (1.1 - i * 0.2), hy + 1, S * (1.0 - i * 0.15))));
            kit.add(ico, SNOW[(i + 1) % 3] as string, place(x, p.y + hy - 0.02, z, a, new THREE.Vector3(S * (0.7 - i * 0.15), 0.16, S * (0.6 - i * 0.12))));
          }
        } else {
          // Above the face: the mountain's snow going on, high, a pine or a boulder here and there.
          kit.add(slab, ROCK[Math.floor(k * 3.99)] as string, place(p.x, p.y + top * 0.5 - 0.5, p.z, k * 3, new THREE.Vector3(S * 1.02, top + 1, S * 0.98)));
          kit.add(slab, SNOW[into % 2] as string, place(p.x, p.y + top, p.z, k * 3, new THREE.Vector3(S * 1.34, 0.08, S * 1.3)));
          if (k > 0.88 && into >= 1) this.pine(kit, pineCone, p.x + (k2 - 0.5) * 0.6, p.y + top, p.z + (k - 0.5) * 0.6, 1.1 + k2 * 0.9, k);
          else if (k2 > 0.9) kit.add(ico, STONE[Math.floor(k * 2.99)] as string, place(p.x, p.y + top + 0.25, p.z, k * 5, new THREE.Vector3(0.9, 0.6, 0.8)));
        }
      } else if (kind === 'ledge') {
        // The slingers' ledge across the fog: a shelf of rock, its own crag under it.
        kit.add(slab, ROCK_D[Math.floor(k * 2.99)] as string, place(p.x, p.y - 0.5, p.z, k * 3, new THREE.Vector3(S * 1.05, 0.7, S * 1.0)));
        kit.add(slab, SNOW[Math.floor(k2 * 2.99)] as string, place(p.x, p.y - 0.13, p.z, k * 3, new THREE.Vector3(S * 1.3, 0.07, S * 1.26)));
        kit.add(cone, ROCK[Math.floor(k2 * 3.99)] as string, place(p.x, p.y - 3.4, p.z, k * 4, new THREE.Vector3(S * 1.1, 5.6, S * 1.0), Math.PI));
        if (k > 0.72) kit.add(ico, ROCK[1] as string, place(p.x + 0.3, p.y + 0.4, p.z, k, new THREE.Vector3(0.9, 1.3, 0.8)));
      }
    }
    ico.dispose();
    slab.dispose();
    cone.dispose();
    pineCone.dispose();
  }

  /** A pine of the south face: three dark tiers, snow lying on each. */
  private pine(kit: Kit, cone: THREE.BufferGeometry, x: number, y: number, z: number, h: number, k: number): void {
    kit.add(cone, '#2b211c', place(x, y + h * 0.08, z, 0, new THREE.Vector3(0.12, h * 0.16, 0.12)));
    for (let i = 0; i < 3; i++) {
      const r = h * (0.34 - i * 0.08);
      const ty = y + h * (0.22 + i * 0.24);
      kit.add(cone, PINE[(i + Math.floor(k * 3)) % 3] as string, place(x, ty + h * 0.16, z, k * 5 + i, new THREE.Vector3(r * 2, h * 0.36, r * 2)));
      kit.add(cone, SNOW[i % 3] as string, place(x, ty + h * 0.24, z, k * 5 + i, new THREE.Vector3(r * 1.55, h * 0.24, r * 1.55)));
    }
  }

  /** How many cells into the cliff a cliff cell is (0 at its face). */
  private cliffDepth(q: number, r: number): number {
    for (let d = 1; d <= 4; d++) if (this.kindAt(q + d, r) !== 'cliff' || this.kindAt(q + d - 1, r + 1) !== 'cliff') return d - 1;
    return 4;
  }

  /* ---------- chapels ---------- */

  /**
   * A chapel of the crag: a block of pale stone, a steep slate roof under snow, its door toward `yaw`, a lamp over it (a
   * glow); `shrine`: the cliff path's niche, its roof out over the way.
   */
  private chapel(kit: Kit, p: THREE.Vector3, yaw: number, size: number, shrine: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const prism = new THREE.CylinderGeometry(0.5, 0.5, 1, 3);
    const w = (shrine ? 1.05 : 1.5) * size;
    const d = (shrine ? 0.85 : 1.4) * size;
    const hgt = (shrine ? 1.05 : 1.3) * size;
    const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    const at = (f: number, up: number, side = 0): THREE.Vector3 => p.clone().addScaledVector(fwd, f).add(new THREE.Vector3(Math.cos(yaw) * side, up, -Math.sin(yaw) * side));
    // Walls of pale stone (a darker footing, quoins at the corners), the door's dark under its lintel.
    const b = at(0, hgt * 0.5);
    kit.add(box, ROCK_D[0] as string, place(b.x, p.y + 0.07, b.z, yaw, new THREE.Vector3(w + 0.14, 0.16, d + 0.14)));
    kit.add(box, '#a7abb2', place(b.x, b.y, b.z, yaw, new THREE.Vector3(w, hgt, d)));
    for (const sx of [-1, 1])
      for (const sz of [-1, 1]) {
        const c = at((sz * d) / 2, hgt * 0.5, (sx * w) / 2);
        kit.add(box, '#8d9198', place(c.x, c.y, c.z, yaw, new THREE.Vector3(0.12, hgt, 0.12)));
      }
    const door = at(d * 0.5 + 0.01, hgt * 0.32);
    kit.add(box, '#2a1f19', place(door.x, door.y, door.z, yaw, new THREE.Vector3(w * 0.32, hgt * 0.62, 0.05)));
    kit.add(box, '#8d9198', place(door.x, door.y + hgt * 0.34, door.z, yaw, new THREE.Vector3(w * 0.46, 0.1, 0.09)));
    // The roof: steep slate, its long snow on both slopes (white from above: what reads first), the ridge dark.
    const roofY = p.y + hgt + hgt * 0.3;
    const rc = at(shrine ? 0.2 * size : 0, 0);
    kit.add(prism, SLATE[Math.floor(this.k(Math.round(p.x), Math.round(p.z)) * 2.99)] as string, place(rc.x, roofY, rc.z, yaw, new THREE.Vector3(w * 0.9, d * (shrine ? 1.5 : 1.2), hgt * 0.75), -Math.PI / 2, 0));
    kit.add(prism, SNOW[2] as string, place(rc.x, roofY + 0.06, rc.z, yaw, new THREE.Vector3(w * 0.78, d * (shrine ? 1.42 : 1.12), hgt * 0.6), -Math.PI / 2, 0));
    // The bell-cote over the door: a small arch of stone, the bell dark in it, an iron cross on top.
    const bc = at(d * 0.5 - 0.05, hgt * 1.62);
    kit.add(box, '#a7abb2', place(bc.x, bc.y, bc.z, yaw, new THREE.Vector3(0.38 * size, 0.46 * size, 0.16)));
    kit.add(box, '#1d1a19', place(bc.x, bc.y - 0.02, bc.z, yaw, new THREE.Vector3(0.18 * size, 0.26 * size, 0.18)));
    kit.add(box, SNOW[0] as string, place(bc.x, bc.y + 0.25 * size, bc.z, yaw, new THREE.Vector3(0.42 * size, 0.06, 0.2)));
    kit.add(box, IRON, place(bc.x, bc.y + 0.42 * size, bc.z, yaw, new THREE.Vector3(0.03, 0.24, 0.03)));
    kit.add(box, IRON, place(bc.x, bc.y + 0.46 * size, bc.z, yaw, new THREE.Vector3(0.13, 0.03, 0.03)));
    // Its lamp over the door, and its warm light spilling out on the snow (glows: no scene light).
    const lamp = at(d * 0.5 + 0.14, hgt * 0.78);
    kit.add(box, '#1c1f24', place(lamp.x, lamp.y, lamp.z, Math.PI / 4, new THREE.Vector3(0.13, 0.17, 0.13)));
    kit.add(box, '#e8eef8', place(lamp.x, lamp.y, lamp.z, Math.PI / 4, new THREE.Vector3(0.09, 0.12, 0.09)));
    this.light(lamp, WARM, 1.6, 0.75, 1);
    const spill = at(d * 0.5 + 0.7, 0.08);
    this.light(spill, WARM, 2.4, 0.22, 0.4);
    box.dispose();
    prism.dispose();
  }

  /* ---------- the cliff path ---------- */

  private buildRidge(kit: Kit, s: BattleState): void {
    if (this.place === 'ridgeFoot') {
      this.chapel(kit, this.hex(-2, 1, 0), Math.PI / 2, 1, true);
      this.chapels.push(this.hex(-2, 1, 0));
      this.lanternsBelow();
      return;
    }
    const L = ridgeLayout(s.seed);
    for (const c of L.chapels) {
      const p = this.ctx.at(c.x, c.y, 0);
      // The shrine's back in the rock, its roof out over the way (east).
      this.chapel(kit, p.clone().add(new THREE.Vector3(-0.35, 0, 0)), Math.PI / 2, 1.25, true);
      this.chapels.push(this.ctx.at(c.x, c.y, 0.05));
    }
    // A waymark of stones at the walkway's head.
    const top = L.way[L.way.length - 1] as { x: number; y: number };
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const t = this.ctx.at(top.x - 600, top.y - 300, 0);
    for (let i = 0; i < 4; i++) kit.add(ico, STONE[i % 3] as string, place(t.x + (i % 2 ? 0.05 : -0.05), t.y + 0.16 + i * 0.2, t.z, i, new THREE.Vector3(0.44 - i * 0.08, 0.26, 0.4 - i * 0.07)));
    ico.dispose();
    this.lanternsBelow();
  }

  /** Three lanterns far below on the terraces' road, coming up behind (Joran's): glows in the fog. */
  private lanternsBelow(): void {
    for (let i = 0; i < 3; i++) this.light(new THREE.Vector3(9 + i * 0.6, -5.2 + i * 0.15, 14 - i * 0.9), WARM, 1.4, 0.75, 1);
  }

  /* ---------- the walkway ---------- */

  private buildWalk(kit: Kit, s: BattleState): void {
    const L = this.place === 'moonWalk' ? walkLayout(s.seed) : walkLayout(3);
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The spans' boards (instanced, their state drawn), the braces under them into the rock.
    L.spans.forEach((sp, i) => {
      const a = this.hex((L.landings[i] as { q: number; r: number }).q, (L.landings[i] as { q: number; r: number }).r);
      const b = this.hex((L.landings[i + 1] as { q: number; r: number }).q, (L.landings[i + 1] as { q: number; r: number }).r);
      const u = b.clone().sub(a).setY(0).normalize();
      const yaw = Math.atan2(u.x, u.z);
      sp.forEach((c, k) => {
        const p = this.hex(c.q, c.r);
        for (let j = -1; j <= 1; j++) this.addBoard(i * sp.length + k, p.clone().addScaledVector(u, j * 0.36), yaw, S * 0.92, 0);
        // A brace under it, slanting down into the cliff (west).
        kit.add(box, WOOD_D, place(p.x - 0.25, p.y - 0.42, p.z, yaw, new THREE.Vector3(0.08, 0.08, 1.0), 0, 0.75));
        kit.add(box, WOOD_D, place(p.x - 0.55, p.y - 0.75, p.z, 0, new THREE.Vector3(0.1, 0.7, 0.1), 0, -0.6));
      });
      // The rails: a rope along the outer side, posts at each end.
      for (let j = 0; j <= sp.length; j++) {
        const p = a.clone().lerp(b, (j + 0.5) / (sp.length + 1));
        const out = new THREE.Vector3(u.z, 0, -u.x).multiplyScalar(0.55);
        if (out.x < 0) out.multiplyScalar(-1);
        kit.add(box, WOOD_D, place(p.x + out.x, p.y + 0.35, p.z + out.z, yaw, new THREE.Vector3(0.06, 0.7, 0.06)));
      }
      this.spanEnds.push([a.clone().setY(a.y + 0.12), b.clone().setY(b.y + 0.12)]);
    });
    // The posts with their mirrors, on the first four landings.
    L.posts.forEach((p0, i) => {
      const p = this.ctx.at(p0.x, p0.y, 0);
      this.posts.push(this.post(kit, p, i));
    });
    // The chapel on the last landing.
    const end = L.landings[L.landings.length - 1] as { q: number; r: number };
    this.chapel(kit, this.hex(end.q - 1, end.r - 1), Math.PI, 1, false);
    box.dispose();
    // The moon's shaft comes down out of the sky onto the first post, through its one gap in the fog.
    const m = this.ctx.at(L.moon.x, L.moon.y, 0);
    this.moonTop.set(m.x + 3, 12, m.z - 6);
    this.buildBeam();
    this.lanternsBelow();
    this.buildPlanks();
    // (The cinematic: the moon laid on the first span.)
    if (this.place === 'moonMist') this.drawBeam([WALK_DOWN, WALK_ON, WALK_ON, WALK_ON]);
  }

  /** A post of the walkway: a squared timber, its iron yoke and the steel mirror in it (turned by its state). */
  private post(kit: Kit, p: THREE.Vector3, i: number): PostView {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, WOOD[i % 3] as string, place(p.x, p.y + 0.85, p.z, 0.2, new THREE.Vector3(0.16, 1.7, 0.16)));
    kit.add(box, IRON, place(p.x, p.y + 0.2, p.z, 0.2, new THREE.Vector3(0.2, 0.08, 0.2)));
    box.dispose();
    const g = new THREE.Group();
    g.position.set(p.x, p.y + 1.85, p.z);
    const steel = this.track(new THREE.MeshStandardMaterial({ color: '#e7edf6', metalness: 0.9, roughness: 0.12, emissive: '#7a8cad', emissiveIntensity: 0.35 }));
    const iron = this.track(new THREE.MeshStandardMaterial({ color: IRON, metalness: 0.5, roughness: 0.45 }));
    const yoke = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.34, 0.03, 5, 20, Math.PI)), iron);
    yoke.rotation.z = Math.PI;
    const disc = new THREE.Group();
    const face = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.3, 0.3, 0.035, 20)), steel);
    face.rotation.x = Math.PI / 2;
    const rim = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.31, 0.025, 4, 20)), iron);
    disc.add(face, rim);
    g.add(yoke, disc);
    this.group.add(g);
    return { g, disc, yaw: 0, tilt: 0, wantYaw: 0, wantTilt: 0, top: g.position.clone() };
  }

  /** The landing of the campfire (its scene): the end chapel, a post, the walkway going down behind. */
  private buildFireLanding(kit: Kit): void {
    this.chapel(kit, this.hex(0, -4), 0, 1.15, false);
    this.posts.push(this.post(kit, this.hex(2, 1), 0));
    this.light(this.hex(0, 0, 0.6), WARM, 3.2, 0.5, 1);
    this.lanternsBelow();
  }

  /* ---------- the chapels' bridge ---------- */

  private buildBridge(kit: Kit, s: BattleState): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const ring = new THREE.TorusGeometry(0.07, 0.022, 4, 8);
    if (this.place === 'chainBridge') {
      const L = bridgeLayout(s.seed);
      // Each plank across the bridge: two boards side by side, end to end across it.
      L.planks.forEach((k, i) => {
        const y = k.r * 866;
        const p = this.ctx.at(0, y, 0);
        for (const j of [-1, 1]) this.addBoard(i, p.clone().add(new THREE.Vector3(0, 0, j * 0.24)), 0, S * 1.35, j);
      });
      // The chapels: the near one at the foot behind its crag's yard, the far one at the top with the mangonel.
      this.chapel(kit, this.ctx.at(-1600, (BRIDGE_ROWS + 3) * 866, 0), Math.PI, 1.1, false);
      this.chapel(kit, this.ctx.at(1700, -(BRIDGE_ROWS + 3) * 866, 0), 0, 1.1, false);
      this.chapels.push(this.ctx.at(-1600, (BRIDGE_ROWS + 3) * 866, 0));
      this.chains(kit, ring, box, (BRIDGE_ROWS + 0.7) * 866, -(BRIDGE_ROWS + 0.7) * 866);
      this.buildPlanks();
    } else {
      // The close: the far chapel's yard; the bridge going down to the south over the drop.
      this.chapel(kit, this.hex(0, -4), 0, 1.2, false);
      for (let r = 2; r <= 9; r++) {
        const p = this.ctx.at(0, r * 866, 0);
        for (const j of [-1, 1]) kit.add(box, WOOD[(r + j + 2) % 3] as string, place(p.x, p.y + 0.04, p.z + j * 0.24, 0, new THREE.Vector3(S * 1.35, 0.08, 0.38)));
      }
      this.chains(kit, ring, box, 9.5 * 866, 1.6 * 866);
    }
    box.dispose();
    ring.dispose();
    this.lanternsBelow();
  }

  /** The two chains along the bridge's sides, sagging between the crags, and the ropes down to its planks. */
  private chains(kit: Kit, ring: THREE.BufferGeometry, box: THREE.BufferGeometry, y0: number, y1: number): void {
    for (const side of [-1, 1]) {
      const x = side * 760;
      const n = Math.round(Math.abs(y1 - y0) / 230);
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const y = y0 + (y1 - y0) * t;
        const p = this.ctx.at(x, y, 0);
        const sag = 1.05 - Math.sin(t * Math.PI) * 0.55;
        kit.add(ring, '#4a4f57', place(p.x, p.y + sag, p.z, 0, 1, i % 2 ? Math.PI / 2 : 0));
        if (i % 3 === 0) kit.add(box, '#7a6a52', place(p.x - side * 0.03, p.y + sag * 0.5, p.z, 0, new THREE.Vector3(0.025, sag, 0.025)));
      }
      // The anchor posts at both ends.
      for (const y of [y0, y1]) {
        const p = this.ctx.at(x, y, 0);
        kit.add(box, WOOD_D, place(p.x, p.y + 0.6, p.z, 0, new THREE.Vector3(0.18, 1.2, 0.18)));
      }
    }
  }

  /* ---------- the planks ---------- */

  private addBoard(plank: number, at: THREE.Vector3, yaw: number, len: number, side: number): void {
    const m = new THREE.Matrix4();
    this.boards.push({ plank, m, at: at.clone().setY(at.y + 0.045), yaw, len, side });
  }

  private buildPlanks(): void {
    if (!this.boards.length) return;
    const geo = this.track(new THREE.BoxGeometry(1, 1, 1));
    const mat = this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.82, metalness: 0.05 }));
    const im = new THREE.InstancedMesh(geo, mat, this.boards.length);
    im.name = 'pasarela-planks';
    this.planks = im;
    this.boards.forEach((b, i) => {
      b.m.compose(b.at, new THREE.Quaternion().setFromEuler(new THREE.Euler(0, b.yaw, 0)), new THREE.Vector3(b.len, 0.08, 0.3));
      im.setMatrixAt(i, b.m);
      im.setColorAt(i, WOOD_DARK);
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    this.group.add(im);
  }

  /** The boards by their planks' state (only when it changes): ink, burning, creeping back, torn, laid again. */
  private drawPlanks(s: BattleState): void {
    const im = this.planks;
    const r = s.rules?.senda?.litter;
    const p = s.senda?.litter?.pas;
    if (!im || !r || !p) return;
    const W = r.walk;
    const step = (v: number): number => Math.round(v * 8);
    let key = p.torn.join('') + '|' + p.mend + ':' + step(p.mendT / Math.max(1, (W ?? r.span)?.mend ?? 1));
    if (W) for (let i = 0; i < W.spans.length; i++) key += `|${step((p.clean[i] ?? 0) / W.burn)},${step((p.dark[i] ?? 0) / W.cover)}`;
    if (key === this.plankKey) return;
    this.plankKey = key;
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const sc = new THREE.Vector3();
    this.boards.forEach((b, i) => {
      const torn = !!p.torn[b.plank];
      let len = b.len;
      if (torn) {
        // Gone: a stub left at each end (the side boards on the bridge), or growing back while the hero lays it.
        const grow = p.mend === b.plank ? Math.min(1, p.mendT / Math.max(1, (W ?? r.span)?.mend ?? 1)) : 0;
        len = b.len * Math.max(0.16, grow);
      }
      e.set(0, b.yaw, 0);
      q.setFromEuler(e);
      sc.set(len, 0.08, 0.3);
      this.m4.compose(b.at, q, sc);
      im.setMatrixAt(i, this.m4);
      // Its colour: the walkway's by its span's ink; the bridge's wood.
      if (W) {
        const sp = Math.floor(b.plank / 4);
        const clean = (p.clean[sp] ?? 0) / W.burn;
        const dark = (p.dark[sp] ?? 0) / W.cover;
        if (clean < 1) this.c.copy(INK_PLANK).lerp(WOOD_LIT, clean * 0.85);
        else if (dark > 0) this.c.copy(p.lit === sp ? WOOD_LIT : WOOD_DARK).lerp(INK_PLANK, dark * dark);
        else this.c.copy(p.lit === sp ? WOOD_LIT : WOOD_DARK);
      } else this.c.set(WOOD[(b.plank + (b.side > 0 ? 1 : 0)) % 3] as string);
      if (torn) this.c.multiplyScalar(0.7);
      im.setColorAt(i, this.c);
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
  }

  /* ---------- the moon's beam ---------- */

  private buildBeam(): void {
    this.beamMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 } },
        vertexShader: /* glsl */ `attribute float aU; attribute float aV; attribute float aA; varying float vU; varying float vV; varying float vA;
          void main() { vU = aU; vV = aV; vA = aA; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uT; varying float vU; varying float vV; varying float vA;
          void main() {
            float x = abs(vV);
            float core = smoothstep(0.38, 0.1, x);
            float halo = (1.0 - x) * (1.0 - x);
            float pulse = smoothstep(0.6, 1.0, sin(vU * 1.4 - uT * 4.0));
            vec3 c = mix(vec3(0.62, 0.74, 1.0), vec3(1.0), core);
            float a = (halo * 0.5 + core * 0.9 + pulse * halo * 0.4) * vA;
            gl_FragColor = vec4(c * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    this.beam = new THREE.Mesh(this.track(new THREE.BufferGeometry()), this.beamMat);
    this.beam.renderOrder = 4;
    this.beam.frustumCulled = false;
    this.group.add(this.beam);
    // The shaft from the sky onto the first post: a tall cone of cold light, brightest at its foot.
    const p0 = this.posts[0]?.top ?? new THREE.Vector3();
    const len = p0.distanceTo(this.moonTop);
    const geo = this.track(new THREE.CylinderGeometry(0.22, 1.6, len, 16, 1, true));
    const mat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 } },
        vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uT; varying vec2 vUv;
          void main() {
            float a = pow(1.0 - vUv.y, 1.6) * 0.26 + 0.04;
            a *= 0.8 + 0.2 * sin(vUv.x * 40.0 + uT * 0.7);
            gl_FragColor = vec4(vec3(0.78, 0.86, 1.0) * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    this.shaft = new THREE.Mesh(geo, mat);
    this.shaft.position.copy(p0).lerp(this.moonTop, 0.5);
    this.shaft.lookAt(this.moonTop);
    this.shaft.rotateX(Math.PI / 2);
    this.shaft.renderOrder = 4;
    this.group.add(this.shaft);
  }

  /** The beam's strip from its corners (world points; `a` its strength at each). */
  private rebuildBeam(pts: { p: THREE.Vector3; a: number; w: number }[]): void {
    if (!this.beam) return;
    const pos: number[] = [];
    const u: number[] = [];
    const v: number[] = [];
    const al: number[] = [];
    const idx: number[] = [];
    let run = 0;
    for (let i = 0; i + 1 < pts.length; i++) {
      const A = pts[i] as { p: THREE.Vector3; a: number; w: number };
      const B = pts[i + 1] as { p: THREE.Vector3; a: number; w: number };
      const dx = B.p.x - A.p.x;
      const dz = B.p.z - A.p.z;
      const len = Math.hypot(dx, dz) || 1;
      const nxA = (-dz / len) * A.w;
      const nzA = (dx / len) * A.w;
      const nxB = (-dz / len) * B.w;
      const nzB = (dx / len) * B.w;
      const k = pos.length / 3;
      pos.push(A.p.x + nxA, A.p.y, A.p.z + nzA, A.p.x - nxA, A.p.y, A.p.z - nzA, B.p.x + nxB, B.p.y, B.p.z + nzB, B.p.x - nxB, B.p.y, B.p.z - nzB);
      const l3 = A.p.distanceTo(B.p);
      u.push(run, run, run + l3, run + l3);
      v.push(1, -1, 1, -1);
      al.push(A.a, A.a, B.a, B.a);
      idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      run += l3;
    }
    const g = this.beam.geometry;
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('aU', new THREE.Float32BufferAttribute(u, 1));
    g.setAttribute('aV', new THREE.Float32BufferAttribute(v, 1));
    g.setAttribute('aA', new THREE.Float32BufferAttribute(al, 1));
    g.setIndex(idx);
    g.computeBoundingSphere();
  }

  /** The beam by the mirrors: post to post high while on, down along the span of the one down, back to the moon. */
  private drawBeam(turns: readonly number[]): void {
    const key = turns.join('');
    if (key === this.beamKey || !this.posts.length) return;
    this.beamKey = key;
    const pts: { p: THREE.Vector3; a: number; w: number }[] = [];
    const lit = walkLit(turns);
    for (let i = 0; i < this.posts.length; i++) {
      const top = (this.posts[i] as PostView).top;
      pts.push({ p: top.clone(), a: 1, w: 0.42 });
      const t = turns[i];
      if (t === WALK_DOWN) {
        // Laid down along its span: onto the planks, a wide band to the next landing.
        const [a, b] = this.spanEnds[i] ?? [];
        if (a && b) {
          pts.push({ p: a.clone().setY(a.y + 0.05), a: 1, w: 0.75 }, { p: b.clone().setY(b.y + 0.05), a: 0.9, w: 0.75 });
        }
        break;
      }
      if (t === WALK_BACK) {
        // Back where it came from, toward the moon, fading.
        const prev = i > 0 ? (this.posts[i - 1] as PostView).top : this.moonTop;
        pts.push({ p: top.clone().lerp(prev, 0.6), a: 0, w: 0.3 });
        break;
      }
      // On: to the next post (or on past the last, into the fog).
      if (i + 1 >= this.posts.length) pts.push({ p: top.clone().add(new THREE.Vector3(0, 0, -3)), a: 0, w: 0.3 });
    }
    void lit;
    this.rebuildBeam(pts);
    // Each mirror turned to where it sends the beam: down toward its span, on toward the next post, back to the moon.
    this.posts.forEach((pv, i) => {
      const t = turns[i] ?? WALK_ON;
      const from = i > 0 ? (this.posts[i - 1] as PostView).top : this.moonTop;
      const next = t === WALK_DOWN ? (this.spanEnds[i]?.[1] ?? pv.top) : t === WALK_BACK ? from : ((this.posts[i + 1] as PostView | undefined)?.top ?? pv.top.clone().add(new THREE.Vector3(0, 0, -3)));
      const a = from.clone().sub(pv.top).normalize();
      const b = next.clone().sub(pv.top).normalize();
      const n = t === WALK_BACK ? a : a.add(b).normalize();
      pv.wantYaw = Math.atan2(n.x, n.z);
      pv.wantTilt = -Math.asin(Math.max(-1, Math.min(1, n.y))) ;
    });
  }

  /* ---------- the fog below, the pool, the warnings ---------- */

  /**
   * The fog's noise, made once: a tileable value-noise texture (four octaves baked in), sampled twice a pixel by the sea
   * and once by the mist. Noise worked out in the shader per pixel, on three layers across the whole screen, doubled a
   * phone's frame cost (docs/RENDIMIENTO.md, the cleric's chapter 2).
   */
  private noiseTex(): THREE.DataTexture {
    const N = 128;
    const data = new Uint8Array(N * N * 4);
    const lat = (o: number): number[] => {
      const g: number[] = [];
      for (let i = 0; i < o * o; i++) g.push(Math.sin(i * 12.9898 + o * 78.233) * 43758.5453 % 1);
      return g.map((v) => Math.abs(v));
    };
    const octs = [4, 8, 16, 32].map((o) => ({ o, g: lat(o) }));
    for (let y = 0; y < N; y++)
      for (let x = 0; x < N; x++) {
        let v = 0;
        let a = 0.5;
        let tot = 0;
        for (const { o, g } of octs) {
          const fx = (x / N) * o;
          const fy = (y / N) * o;
          const ix = Math.floor(fx);
          const iy = Math.floor(fy);
          const tx = fx - ix;
          const ty = fy - iy;
          const sx = tx * tx * (3 - 2 * tx);
          const sy = ty * ty * (3 - 2 * ty);
          const at = (i: number, j: number): number => g[((j % o) * o + (i % o)) | 0] as number;
          const top = at(ix, iy) * (1 - sx) + at(ix + 1, iy) * sx;
          const bot = at(ix, iy + 1) * (1 - sx) + at(ix + 1, iy + 1) * sx;
          v += (top * (1 - sy) + bot * sy) * a;
          tot += a;
          a *= 0.5;
        }
        const b = Math.round((v / tot) * 255);
        const k = (y * N + x) * 4;
        data[k] = data[k + 1] = data[k + 2] = b;
        data[k + 3] = 255;
      }
    const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.magFilter = THREE.LinearFilter;
    t.minFilter = THREE.LinearFilter;
    t.needsUpdate = true;
    this.tex = t;
    return t;
  }

  private buildFog(): void {
    const tex = this.noiseTex();
    this.fogMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 }, uN: { value: tex } },
        vertexShader: FOG_VERT,
        fragmentShader: /* glsl */ `uniform float uT; uniform sampler2D uN; varying vec2 vP;
          void main() {
            float n = texture2D(uN, vP * 0.012 + vec2(uT * 0.0016, uT * 0.0007)).r;
            float n2 = texture2D(uN, vP * 0.027 - vec2(uT * 0.0011, -uT * 0.0005) + 0.37).r;
            float m = smoothstep(0.3, 0.75, n * 0.6 + n2 * 0.4);
            vec3 c = mix(vec3(0.24, 0.29, 0.4), vec3(0.6, 0.66, 0.79), m);
            // The moon's silver on it, toward the moon (east and north).
            float side = smoothstep(-25.0, 30.0, vP.x * 0.8 - vP.y * 0.6);
            c += vec3(0.36, 0.4, 0.5) * side * m;
            gl_FragColor = vec4(c, 1.0);
          }`,
        fog: false,
      }),
    );
    const sea = new THREE.Mesh(this.track(new THREE.PlaneGeometry(220, 220, 1, 1)), this.fogMat);
    sea.rotation.x = -Math.PI / 2;
    sea.position.y = this.ctx.ground(0, 0) - 4.6;
    sea.renderOrder = -5;
    this.group.add(sea);
    // Mist drifting just under the ledges' lips: one sheet, see-through where the noise is thin.
    this.mistMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 }, uA: { value: 0.65 }, uN: { value: tex } },
        vertexShader: FOG_VERT,
        fragmentShader: /* glsl */ `uniform float uT; uniform float uA; uniform sampler2D uN; varying vec2 vP;
          void main() {
            float n = texture2D(uN, vP * 0.021 + vec2(uT * 0.004, uT * 0.0013)).r;
            float a = smoothstep(0.45, 0.8, n) * uA;
            gl_FragColor = vec4(vec3(0.7, 0.76, 0.88) * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        fog: false,
      }),
    );
    const sheet = new THREE.Mesh(this.track(new THREE.PlaneGeometry(160, 160, 1, 1)), this.mistMat);
    sheet.rotation.x = -Math.PI / 2;
    sheet.position.y = this.ctx.ground(0, 0) - 1.9;
    sheet.renderOrder = -4;
    this.group.add(sheet);
  }

  private buildPool(): void {
    this.poolMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uA: { value: 0.22 } },
        vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uA; varying vec2 vUv;
          void main() { float d = length(vUv - 0.5) * 2.0; float a = smoothstep(1.0, 0.1, d) * uA; gl_FragColor = vec4(vec3(1.0, 0.9, 0.8) * a, a); }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.pool = new THREE.Mesh(this.track(new THREE.PlaneGeometry(1, 1)), this.poolMat);
    this.pool.rotation.x = -Math.PI / 2;
    this.pool.renderOrder = 3;
    this.pool.frustumCulled = false;
    this.group.add(this.pool);
  }

  private buildWarnings(): void {
    const vert = /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
    // A red ring that tightens and fills as the stones come (uK: 0 just warned, 1 landing).
    this.warnMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uK: { value: 0 }, uT: { value: 0 } },
        vertexShader: vert,
        fragmentShader: /* glsl */ `uniform float uK; uniform float uT; varying vec2 vUv;
          void main() {
            float d = length(vUv - 0.5) * 2.0;
            float ring = smoothstep(0.86, 0.93, d) * smoothstep(1.0, 0.95, d);
            float fill = smoothstep(1.0, 0.0, d) * uK * 0.55;
            float a = (ring * (0.75 + 0.25 * sin(uT * 12.0)) + fill) * (d < 1.0 ? 1.0 : 0.0);
            gl_FragColor = vec4(vec3(1.0, 0.31, 0.23) * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    // The stone's shadow: dark, growing (normal blending: it darkens what is under it).
    const shadow = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uK: { value: 0 } },
        vertexShader: vert,
        fragmentShader: /* glsl */ `uniform float uK; varying vec2 vUv;
          void main() { float d = length(vUv - 0.5) * 2.0; float a = smoothstep(1.0, 0.55, d) * (0.25 + 0.55 * uK); gl_FragColor = vec4(0.03, 0.02, 0.05, a); }`,
        transparent: true,
        depthWrite: false,
      }),
    );
    const disc = this.track(new THREE.PlaneGeometry(1, 1));
    for (let i = 0; i < 3; i++) {
      const m = new THREE.Mesh(disc, i === 0 ? this.warnMat : shadow);
      m.rotation.x = -Math.PI / 2;
      m.renderOrder = 5;
      m.visible = false;
      m.frustumCulled = false;
      this.warns.push(m);
      this.group.add(m);
    }
    // The chapels' shelter: a faint cold disc on the snow (brighter while a volley is coming).
    if (this.chapels.length && this.place === 'ridgePath') {
      this.shadeMat = this.track(
        new THREE.ShaderMaterial({
          uniforms: { uA: { value: 0.18 } },
          vertexShader: vert,
          fragmentShader: /* glsl */ `uniform float uA; varying vec2 vUv;
            void main() {
              float d = length(vUv - 0.5) * 2.0;
              float a = (smoothstep(1.0, 0.6, d) * 0.5 + smoothstep(0.9, 0.96, d) * smoothstep(1.0, 0.97, d)) * uA;
              gl_FragColor = vec4(vec3(0.75, 0.84, 1.0) * a, a);
            }`,
          transparent: true,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      for (const c of this.chapels) {
        const m = new THREE.Mesh(disc, this.shadeMat);
        m.rotation.x = -Math.PI / 2;
        m.position.copy(c).setY(c.y + 0.04);
        const d = 1500 * K * 2;
        m.scale.set(d, d, 1);
        m.renderOrder = 3;
        this.group.add(m);
      }
    }
    // The mangonel's stone in the air.
    if (this.place === 'chainBridge') {
      this.stone = new THREE.Mesh(this.track(new THREE.IcosahedronGeometry(0.28, 0)), this.track(new THREE.MeshStandardMaterial({ color: '#4a4550', roughness: 0.9, flatShading: true, emissive: '#2a1a3a', emissiveIntensity: 0.5 })));
      this.stone.visible = false;
      this.group.add(this.stone);
    }
  }

  /* ---------- the board, repainted ---------- */

  /** The board's one point light, made the stretcher's lantern. */
  private pl: THREE.PointLight | null = null;
  lightBase = 5;

  takeLight(f: THREE.PointLight): void {
    this.pl = f;
    f.color.set('#ffdcb8');
    f.distance = 7;
    f.decay = 1.6;
    this.lightBase = 5;
    f.intensity = this.lightBase;
    f.position.copy(this.hex(0, 0, 1.6));
  }

  apply(terrain: TerrainBuild, hour: Hour = 'night'): void {
    this.terrain = terrain;
    // The board's tiles and its outskirts put away: this place draws its own ground (ledges, planks, the cliff, the drop).
    terrain.mesh.visible = false;
    terrain.decor.traverse((o) => {
      if (o !== terrain.decor) o.visible = false;
    });
    if (terrain.fog) terrain.fog.group.visible = false;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    if (this.place === 'ridgePath' || this.place === 'ridgeFoot') {
      this.fall = new SnowFall(this.ctx.low ? 300 : 700, new THREE.Vector2(0.45, 0.2), 0.3);
      this.group.add(this.fall.points);
    }
    this.paint(hour);
  }

  paint(hour: Hour): void {
    void hour;
    const scene = this.ctx.scene;
    scene.background = new THREE.Color('#0b1120');
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set('#1a2336');
      scene.fog.near = 30;
      scene.fog.far = 110;
    }
    const { hemi, key, rim } = this.ctx;
    // Moonlight: cold and clear, strong enough that the snow and the rock read (never a black scene).
    if (hemi) {
      hemi.color.set('#8fa4d0');
      hemi.groundColor.set('#141826');
      hemi.intensity = 1.55;
    }
    if (key) {
      key.color.set('#c8d6ff');
      key.intensity = 1.35;
      key.position.set(14, 18, -10);
    }
    if (rim) {
      rim.color.set('#7f9bff');
      rim.intensity = 1.0;
    }
    // The sky: night, the moon full and low to the north-east (the sky's sun term draws its disc and its halo).
    this.dome?.set('#03060d', '#1b2740', '#f2f6ff');
    if (this.dome) this.dome.u.uSunDir.value.set(0.55, 0.32, -0.75);
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'volley') {
        // The stones land: on the stretcher in the open, a spray of grit; in a chapel's shelter, on its roof.
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: e.n ? 22 : 10, color: '#8a8d93', speed: 2.2, up: 1.4, size: 0.12, life: 12, gravity: 1 });
        if (e.n) this.ctx.shake(0.18);
        const m = s.senda?.litter?.pas?.marks;
        void m;
      } else if (e.kind === 'volleyWarn') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.15), 0.3, 1.2, '#ff4f3a', 8, 0.8);
      } else if (e.kind === 'pasGo') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.15), 0.2, 1.4, '#f4f8ff', 6, 0.7);
      } else if (e.kind === 'moon' || e.kind === 'mirror') {
        const pv = this.posts[e.n ?? 0];
        if (pv) this.ctx.vfx.burst(pv.top, { n: 12, color: '#e8f0ff', speed: 1.6, up: 0.6, size: 0.08, life: 8 });
        this.beamKey = '';
      } else if (e.kind === 'clean') {
        // A span burnt clean: a white flash along it, sparks of ink going up.
        const ends = this.spanEnds[e.n ?? 0];
        if (ends) {
          const [a, b] = ends as [THREE.Vector3, THREE.Vector3];
          for (let i = 0; i <= 4; i++) this.ctx.vfx.burst(a.clone().lerp(b, i / 4), { n: 6, color: '#b9a6ff', speed: 1.2, up: 1.6, size: 0.08, life: 12 });
          this.ctx.flare(a.clone().lerp(b, 0.5).setY(a.y + 0.6), '#f4f8ff', 2.2, 10);
        }
      } else if (e.kind === 'inked') {
        const ends = this.spanEnds[e.n ?? 0];
        if (ends) this.ctx.vfx.ring((ends[0] as THREE.Vector3).clone().lerp(ends[1] as THREE.Vector3, 0.5), 0.3, 2.4, '#6a4aa8', 10, 0.8);
      } else if (e.kind === 'fall') {
        // Down off the planks into the nets: a splash of snow and ink, the nets' shake below.
        this.ctx.vfx.burst(p, { n: 16, color: '#c8d3e1', speed: 1.8, up: 0.4, size: 0.14, life: 12, gravity: 2 });
        this.ctx.shake(0.22);
      } else if (e.kind === 'slip') {
        this.ctx.flare(p.clone().setY(p.y + 0.8), '#ff4f3a', 1.8, 10);
        this.ctx.shake(0.15);
      } else if (e.kind === 'saved') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.3, 1.6, '#f4f8ff', 8, 0.8);
        this.ctx.flare(p.clone().setY(p.y + 0.8), '#ffffff', 1.6, 10);
      } else if (e.kind === 'dropped') {
        this.ctx.vfx.burst(p, { n: 20, color: '#c8d3e1', speed: 2, up: 0.2, size: 0.15, life: 16, gravity: 3 });
      } else if (e.kind === 'tear') {
        // A plank torn off: splinters.
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.1), { n: 18, color: '#6e5c4c', speed: 2.4, up: 1.2, size: 0.1, life: 14, gravity: 2 });
      } else if (e.kind === 'plank') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.12), 0.2, 1.0, '#f4f8ff', 6, 0.7);
      } else if (e.kind === 'stone') {
        this.ctx.flare(p.clone().setY(p.y + 0.4), '#ffd6c8', 2.6, 10);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: 24, color: '#5a5560', speed: 3, up: 1.6, size: 0.14, life: 14, gravity: 2 });
        this.ctx.shake(0.4);
      } else if (e.kind === 'overGap') {
        this.ctx.flare(p.clone().setY(p.y + 1.0), '#ff4f3a', 2.0, 10);
      } else if (e.kind === 'gone') {
        this.ctx.vfx.burst(p, { n: 30, color: '#c8d3e1', speed: 2.6, up: 0.6, size: 0.16, life: 18, gravity: 3 });
        this.ctx.shake(0.5);
      } else if (e.kind === 'end') {
        this.ctx.flare(p.clone().setY(p.y + 1.2), '#f4f8ff', 2.4, 12);
      } else if (e.kind === 'set') {
        this.ctx.vfx.burst(p, { n: 10, color: '#c8d3e1', speed: 1.2, up: 0.8, size: 0.16, life: 10, smoke: true });
      } else if (e.kind === 'lifted') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.15), 0.2, 1.0, '#e8f0ff', 6, 0.7);
      } else if (e.kind === 'peel') {
        this.ctx.flare(p.clone().setY(p.y + 0.5), '#ffffff', 1.6, 8);
      } else if (e.kind === 'limpetOut') {
        this.ctx.vfx.burst(p, { n: 12, color: '#120c1c', speed: 1.4, up: 1.4, size: 0.18, life: 12, gravity: 1 });
      } else if (e.kind === 'mended') {
        const t = s.senda?.npcs.find((n) => n.id === 'tessa');
        if (t) this.ctx.vfx.beam(p.clone().setY(p.y + 0.7), this.ctx.at(t.x, t.y, 0.9), '#e8f0ff', 3, 0.05);
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    const w = s.senda?.litter;
    const r = s.rules?.senda?.litter;
    if (!w || !r) return;
    // The lantern on Tessa's pole carries the board's one light, and its pool on the ground.
    if (this.pl) this.pl.position.copy(this.ctx.at(w.x, w.y, 1.4));
    if (this.pool) {
      const p = this.ctx.at(w.x, w.y, 0.08);
      this.pool.position.copy(p);
      this.pool.scale.set(5.2, 5.2, 1);
    }
    if (r.walk) this.drawBeam(w.turns);
    this.drawPlanks(s);
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    this.fall?.frame(now, camera);
    const tw = now % 600;
    if (this.fogMat) (this.fogMat.uniforms.uT as THREE.IUniform<number>).value = tw;
    if (this.mistMat) (this.mistMat.uniforms.uT as THREE.IUniform<number>).value = tw;
    if (this.beamMat) (this.beamMat.uniforms.uT as THREE.IUniform<number>).value = now % 120;
    if (this.shaft) ((this.shaft.material as THREE.ShaderMaterial).uniforms.uT as THREE.IUniform<number>).value = now % 120;
    if (this.warnMat) (this.warnMat.uniforms.uT as THREE.IUniform<number>).value = now % 120;
    // The mirrors swing to where they are set.
    for (const pv of this.posts) {
      let d = pv.wantYaw - pv.yaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      pv.yaw += d * Math.min(1, dt * 6);
      pv.tilt += (pv.wantTilt - pv.tilt) * Math.min(1, dt * 6);
      pv.disc.rotation.set(pv.tilt, pv.yaw, 0, 'YXZ');
    }
    const s = this.lastState;
    const w = s?.senda?.litter;
    const p = w?.pas;
    const r = s?.rules?.senda?.litter;
    // The warnings: the volley's ring under the hero, the stones' shadows growing on the planks.
    for (const m of this.warns) m.visible = false;
    if (s && p && r) {
      const V = r.ridge?.volley;
      const mx = p.marks[0];
      const my = p.marks[1];
      if (V && mx !== undefined && my !== undefined) {
        const m = this.warns[0] as THREE.Mesh;
        const k = 1 - Math.max(0, Math.min(1, (p.volley - s.waveT) / V.warn));
        m.visible = true;
        m.position.copy(this.ctx.at(mx, my, 0.06));
        const d = V.r * K * 2;
        m.scale.set(d, d, 1);
        if (this.warnMat) (this.warnMat.uniforms.uK as THREE.IUniform<number>).value = k;
      }
      if (this.shadeMat) (this.shadeMat.uniforms.uA as THREE.IUniform<number>).value = p.marks.length ? 0.42 + 0.12 * Math.sin(now * 8) : 0.16;
      const M = r.span?.mangonel;
      if (M) {
        let n = 1;
        for (const st of p.stones) {
          const m = this.warns[n++];
          if (!m) break;
          const k = 1 - Math.max(0, Math.min(1, (st.at - s.waveT) / M.warn));
          m.visible = true;
          m.position.copy(this.ctx.at(st.x, st.y, 0.12));
          const d = M.r * K * 2 * (0.6 + 0.4 * k);
          m.scale.set(d, d, 1);
          ((m.material as THREE.ShaderMaterial).uniforms.uK as THREE.IUniform<number>).value = k;
          // The stone itself on its arc from the mangonel.
          if (this.stone) {
            const from = this.ctx.at(M.x, M.y, 1.6);
            const to = this.ctx.at(st.x, st.y, 0.2);
            this.stone.visible = true;
            this.stone.position.copy(from).lerp(to, k);
            this.stone.position.y += Math.sin(k * Math.PI) * 6;
            this.stone.rotation.set(now * 4, now * 3, 0);
          }
        }
        if (!p.stones.length && this.stone) this.stone.visible = false;
      }
    }
    this.glow.begin();
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    // The moon on the first post's mirror, and on each mirror it reaches.
    if (this.posts.length) {
      const turns = w?.turns ?? [WALK_DOWN];
      for (let i = 0; i < this.posts.length; i++) {
        const pv = this.posts[i] as PostView;
        this.glow.add(pv.top.x, pv.top.y, pv.top.z, MOON, 1.6, 0.9);
        this.glow.add(pv.top.x, pv.top.y, pv.top.z, COLD, 3.2, 0.2);
        const t = turns[i];
        if (t !== WALK_ON) break;
      }
      // The lit span: the moon's white along its planks.
      const lit = p?.lit ?? (this.place === 'moonMist' ? 0 : -1);
      const ends = lit >= 0 ? this.spanEnds[lit] : undefined;
      if (ends) for (let i = 0; i <= 4; i++) {
        const q = (ends[0] as THREE.Vector3).clone().lerp(ends[1] as THREE.Vector3, i / 4);
        this.glow.add(q.x, q.y + 0.1, q.z, MOON, 1.8, 0.35);
      }
    }
    if (s && p && r) {
      // The ink on the walkway's dark spans breathing violet; a plank under a gargoyle pulsing red.
      if (r.walk)
        for (let i = 0; i < r.walk.spans.length; i++) {
          if ((p.clean[i] ?? 0) >= r.walk.burn) continue;
          const ends = this.spanEnds[i];
          if (!ends) continue;
          for (let j = 0; j < 3; j++) {
            const q = (ends[0] as THREE.Vector3).clone().lerp(ends[1] as THREE.Vector3, (j + 0.5) / 3);
            this.glow.add(q.x, q.y + 0.05, q.z, INK, 1.4, 0.3 + 0.1 * Math.sin(now * 1.6 + i + j));
          }
        }
      for (const x of p.perch) {
        if (x.t < 0) continue;
        const q = x.plank >= 0 ? this.plankWorld(r, x.plank) : null;
        if (q) this.glow.add(q.x, q.y + 0.1, q.z, RED, 1.2, 0.5 + 0.3 * Math.sin(now * 10));
      }
      // The stretcher over a gap, Piet hanging: red at them.
      if (p.hang > 0 && w) {
        const q = this.ctx.at(w.x, w.y, 0.3);
        this.glow.add(q.x, q.y, q.z, RED, 2.2, 0.6 + 0.3 * Math.sin(now * 12));
      }
      const piet = s.senda?.npcs.find((n) => n.id === 'piet');
      if (p.slip >= 0 && piet) {
        const q = this.ctx.at(piet.x, piet.y, 0.6);
        this.glow.add(q.x, q.y, q.z, RED, 1.8, 0.55 + 0.3 * Math.sin(now * 10));
      }
      // The lantern's flame on Tessa's pole.
      if (w) {
        const at = this.ctx.at(w.x, w.y, 1.25);
        const fl = 0.9 + 0.1 * Math.sin(now * 9.3);
        this.glow.add(at.x, at.y, at.z, WHITE, 1.2, 0.9 * fl);
        this.glow.add(at.x, at.y, at.z, WARM, 2.6, 0.2 * fl);
      }
    }
    this.glow.end();
  }

  /** A plank's middle on the board. */
  private plankWorld(r: LitterRules, id: number): THREE.Vector3 | null {
    if (r.walk) {
      let n = 0;
      for (const sp of r.walk.spans) {
        if (id < n + sp.length) {
          const h = sp[id - n] as { q: number; r: number };
          return this.hex(h.q, h.r);
        }
        n += sp.length;
      }
      return null;
    }
    const k = r.span?.planks[id];
    return k ? this.ctx.at(0, k.r * 866, 0) : null;
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.fall?.dispose();
    this.tex?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
    void plankAt;
    void this.terrain;
    void this.v3;
  }
}
